// src/pages/admin/PatientDetailPage.jsx

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Activity,
  CheckCircle,
  Circle,
  ClipboardCheck,
  FileText,
  AlertCircle,
  Share2,
} from 'lucide-react';
import { Button } from '../../components/ui';
import { getPhaseData } from '../../data/programData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Exercise types and their display colors
const EXERCISE_TYPES = [
  { key: 'quad', label: 'Quad Test', color: '#60A5FA' },
  { key: 'hamstring', label: 'Hamstring Test', color: '#4ADE80' },
  { key: 'singleLegHop', label: 'Single Leg Hop', color: '#F87171' },
  { key: 'rotationalHop', label: 'Rotational Hop', color: '#FACC15' },
  { key: 'tripleMedialHop', label: 'Triple Medial Hop', color: '#A78BFA' },
];

// Which exercises appear in which phase
const PHASE_EXERCISES = {
  1: ['quad', 'hamstring'],
  2: ['quad', 'hamstring', 'singleLegHop', 'sidePlank'],
  3: ['quad', 'hamstring', 'singleLegHop', 'rotationalHop', 'sidePlank'],
  4: ['quad', 'hamstring', 'singleLegHop', 'rotationalHop', 'tripleMedialHop', 'sidePlank'],
};

// Side Plank Criteria Image Component
function SidePlankCriteria() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Side Plank Criteria</h3>
      </div>
      <div className="p-4">
        <img 
          src="/images/sideplank-criteria.png" 
          alt="Side Plank Criteria by Phase" 
          className="w-full h-auto"
        />
      </div>
    </div>
  );
}

export function PatientDetailPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [patient, setPatient] = useState(null);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [testOuts, setTestOuts] = useState([]);
  const [testOutData, setTestOutData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isSuperAdmin = currentUser?.acl_care?.acl_care_superadmin === true;
  const hasLoggedAudit = useRef(false);

  // Extract best scores from passing attempts
  const getBestScores = (passingAttempts) => {
    const bestScores = {};
    
    passingAttempts.forEach(attempt => {
      if (attempt.scores) {
        Object.entries(attempt.scores).forEach(([key, value]) => {
          if (!bestScores[key] || (value.score > bestScores[key].score)) {
            bestScores[key] = value;
          }
        });
      }
    });
    
    return bestScores;
  };

  // Fetch patient data
  useEffect(() => {
    async function fetchPatientData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch all users and find the patient
        const usersResult = isSuperAdmin
          ? await api.getAllACLUsers()
          : await api.getAllUsers(currentUser.organization_id);

        const users = usersResult.users || usersResult || [];
        const foundPatient = users.find(u => u.id === patientId);

        if (!foundPatient) {
          setError('Patient not found');
          return;
        }

        setPatient(foundPatient);

        // Audit: Patient detail viewed (only once per mount)
        if (!hasLoggedAudit.current) {
          hasLoggedAudit.current = true;
          const patientName = `${foundPatient.first_name || ''} ${foundPatient.last_name || ''}`.trim();
          api.createAuditLog({
            event_type: 'PATIENT_DETAIL_VIEWED',
            user_id: currentUser?.id,
            user_name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || currentUser?.email,
            description: `User viewed patient detail for ${patientName || 'Unknown'}`,
            outcome: 'success',
            patient_id: patientId,
            organization_id: currentUser?.organization_id,
            additional_data: {
              patient_name: patientName,
              patient_email: foundPatient.email,
              current_phase: foundPatient.acl_care?.current_phase || 1
            }
          }).catch(() => {
            // Silently fail - audit logging should not block user actions
          });
        }

        // Fetch training sessions and test outs for this patient
        const [sessionsResult, testOutsResult] = await Promise.all([
          api.getTrainingSessions(patientId).catch(() => ({ items: [] })),
          api.getTestOuts(patientId).catch(() => ({ items: [] })),
        ]);

        const allTestOuts = testOutsResult.items || testOutsResult || [];
        setTrainingSessions(sessionsResult.items || sessionsResult || []);
        setTestOuts(allTestOuts);

        // Process test outs for chart data - group by phase and get best scores
        const testOutsByPhase = {};
        for (let phase = 1; phase <= 4; phase++) {
          const phaseTestOuts = allTestOuts.filter(t => t.phase === phase && t.overall_passed);
          if (phaseTestOuts.length > 0) {
            testOutsByPhase[phase] = getBestScores(phaseTestOuts);
          }
        }
        setTestOutData(testOutsByPhase);

      } catch (err) {
        console.error('[PatientDetailPage] Error fetching data:', err);
        setError('Failed to load patient data');
      } finally {
        setLoading(false);
      }
    }

    fetchPatientData();
    return () => { hasLoggedAudit.current = false; };
  }, [patientId, isSuperAdmin, currentUser.organization_id]);

  // Build chart data
  const buildChartData = () => {
    const data = [];
    
    for (let phase = 1; phase <= 4; phase++) {
      const phaseData = { phase: `Phase ${phase}` };
      const phaseScores = testOutData[phase] || {};
      
      EXERCISE_TYPES.forEach(({ key }) => {
        if (PHASE_EXERCISES[phase].includes(key)) {
          const score = phaseScores[key]?.score;
          phaseData[key] = score != null ? Math.round(score * 100) : null;
        }
      });
      
      data.push(phaseData);
    }
    
    return data;
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            entry.value != null && (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {entry.name}: {entry.value}%
              </p>
            )
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error || 'Patient not found'}</p>
          <Button onClick={() => navigate('/patients')}>
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Unknown';
  const initials = `${(patient.first_name || '?')[0]}${(patient.last_name || '?')[0]}`.toUpperCase();
  const aclCare = patient.acl_care || {};
  const chartData = buildChartData();
  const hasTestOutData = Object.keys(testOutData).length > 0;

  // Data sharing info
  const orgName = currentUser?.organization?.name || 'your organization';
  const dataSharingAccepted = aclCare?.org_data_sharing_accepted;

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/patients')}
          className="p-2 -ml-2 mr-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Client Details</h1>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start">
          {patient.profile_photo ? (
            <img 
              src={patient.profile_photo} 
              alt={fullName}
              className="h-20 w-20 rounded-full object-cover mr-4"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mr-4">
              <span className="text-2xl text-primary font-semibold">{initials}</span>
            </div>
          )}
          
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">{fullName}</h2>
            
            <div className="mt-2 space-y-1">
              {patient.email && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                  <a href={`mailto:${patient.email}`} className="hover:text-primary">
                    {patient.email}
                  </a>
                </div>
              )}
              {patient.phone && (
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2 text-gray-400" />
                  <a href={`tel:${patient.phone}`} className="hover:text-primary">
                    {patient.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Sharing Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Share2 className="h-5 w-5 mr-2 text-primary" />
          Data Sharing
        </h3>
        
        <div className="flex items-center">
          {dataSharingAccepted === true ? (
            <>
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Data sharing enabled</p>
                <p className="text-sm text-gray-500">
                  This client has opted to share their data with {orgName}
                </p>
              </div>
            </>
          ) : dataSharingAccepted === false ? (
            <>
              <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                <Circle className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Data sharing disabled</p>
                <p className="text-sm text-gray-500">
                  This client has opted not to share their data with {orgName}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Preference not set</p>
                <p className="text-sm text-gray-500">
                  This client hasn't set their data sharing preference yet
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Injury Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-primary" />
          Injury Information
        </h3>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Injury Side</span>
            <p className="font-medium text-gray-900 capitalize">
              {aclCare.injury_side || 'Not specified'} {aclCare.injury_side && 'knee'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Surgery Date</span>
            <p className="font-medium text-gray-900">
              {aclCare.surgery_date 
                ? new Date(aclCare.surgery_date).toLocaleDateString() 
                : 'Not specified'
              }
            </p>
          </div>
          <div>
            <span className="text-gray-500">Graft Type</span>
            <p className="font-medium text-gray-900 capitalize">
              {aclCare.graft_type?.replace(/_/g, ' ') || 'Not specified'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Current Phase</span>
            <p className="font-medium text-gray-900">
              Phase {aclCare.current_phase || 1}
            </p>
          </div>
        </div>
      </div>

      {/* Phase Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Phase Progress</h3>
        </div>

        <div className="divide-y divide-gray-100">
          {[1, 2, 3, 4].map((phase) => {
            const phaseKey = `phase_${phase}`;
            const phaseData = aclCare.phases?.[phaseKey];
            const isUnlocked = phaseData?.unlocked;
            const isPassed = phaseData?.test_out_passed;
            const completions = phaseData?.completions || {};
            const targets = getPhaseData(phase)?.targetCompletions || { session_1: 8, session_2: 8 };

            return (
              <div key={phase} className={`p-4 ${!isUnlocked ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {isPassed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    ) : isUnlocked ? (
                      <div className="h-5 w-5 rounded-full border-2 border-primary mr-2 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      </div>
                    ) : (
                      <Circle className="h-5 w-5 text-gray-300 mr-2" />
                    )}
                    <span className="font-medium text-gray-900">Phase {phase}</span>
                    {isPassed && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        Complete
                      </span>
                    )}
                  </div>
                </div>

                {isUnlocked && (
                  <div className="ml-7 grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Session 1</span>
                      <p className="font-medium">
                        {completions.session_1 || 0}/{targets.session_1}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Session 2</span>
                      <p className="font-medium">
                        {completions.session_2 || 0}/{targets.session_2}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Optional</span>
                      <p className="font-medium">
                        {completions.optional || 0}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity Summary */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <ClipboardCheck className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{trainingSessions.length}</p>
          <p className="text-xs text-gray-500">Training Sessions</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <FileText className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {testOuts.filter(t => t.overall_passed).length}
          </p>
          <p className="text-xs text-gray-500">Test Outs Passed</p>
        </div>
      </div>

      {/* Recent Activity */}
      {trainingSessions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Recent Activity</h3>
          </div>

          <div className="divide-y divide-gray-100">
            {trainingSessions.slice(0, 5).map((session) => {
              const date = new Date(session.completed_at);
              const workoutLabel = session.workout === 1 
                ? 'Workout I' 
                : session.workout === 2 
                  ? 'Workout II' 
                  : 'Optional';

              return (
                <div key={session.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      Phase {session.phase} - {workoutLabel}
                    </p>
                    <p className="text-sm text-gray-500">
                      {date.toLocaleDateString()} • {session.duration_minutes || 0} min
                    </p>
                  </div>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Test Out Report */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-primary" />
          Test Out Report
        </h3>
        
        {!hasTestOutData ? (
          <div className="py-8 text-center">
            <p className="text-gray-500">
              No passing test out attempts yet.
            </p>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="phase" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  axisLine={{ stroke: '#D1D5DB' }}
                />
                <YAxis 
                  domain={[0, 100]}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  axisLine={{ stroke: '#D1D5DB' }}
                  tickFormatter={(value) => `${value}`}
                  label={{ 
                    value: 'Score (%)', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { fill: '#6B7280', fontSize: 12 }
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
                />
                {EXERCISE_TYPES.map(({ key, label, color }) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={label}
                    fill={color}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Passing Thresholds Reference */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
        <h3 className="font-semibold text-gray-900 mb-4">Passing Thresholds by Phase</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Exercise</th>
                <th className="px-4 py-2 text-center font-medium text-gray-500">Phase I</th>
                <th className="px-4 py-2 text-center font-medium text-gray-500">Phase II</th>
                <th className="px-4 py-2 text-center font-medium text-gray-500">Phase III</th>
                <th className="px-4 py-2 text-center font-medium text-gray-500">Phase IV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 font-medium text-gray-900">Quad Test</td>
                <td className="px-4 py-3 text-center text-gray-700">≥60%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥70%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥80%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium text-gray-900">Hamstring Test</td>
                <td className="px-4 py-3 text-center text-gray-700">≥60%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥70%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥80%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium text-gray-900">Single Leg Hop</td>
                <td className="px-4 py-3 text-center text-gray-400">—</td>
                <td className="px-4 py-3 text-center text-gray-700">≥80%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium text-gray-900">Rotational Hop</td>
                <td className="px-4 py-3 text-center text-gray-400">—</td>
                <td className="px-4 py-3 text-center text-gray-400">—</td>
                <td className="px-4 py-3 text-center text-gray-700">≥85%</td>
                <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium text-gray-900">Triple Medial Hop</td>
                <td className="px-4 py-3 text-center text-gray-400">—</td>
                <td className="px-4 py-3 text-center text-gray-400">—</td>
                <td className="px-4 py-3 text-center text-gray-400">—</td>
                <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Side Plank Criteria */}
      <SidePlankCriteria />
    </div>
  );
}